import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SmartGoalsGuide() {
  const navigate = useNavigate();

  const smartFramework = [
    {
      letter: "S",
      word: "Specific",
      description: "Your goal should be clear and well-defined. Vague goals like 'get healthier' don't give you direction.",
      question: "What exactly do I want to accomplish? Who is involved? Where will it happen?",
      example: "Instead of 'exercise more,' try 'Complete a 30-minute morning walk in my neighborhood 5 days a week.'"
    },
    {
      letter: "M",
      word: "Measurable",
      description: "Include concrete criteria for measuring progress. If you can't measure it, you can't manage it.",
      question: "How will I know when I've accomplished this goal? What metrics will I track?",
      example: "Track your walks in a journal or app. Count the days each week you completed your goal."
    },
    {
      letter: "A",
      word: "Achievable",
      description: "Your goal should stretch you but remain possible. Setting impossible goals leads to frustration.",
      question: "Is this realistic given my current situation, resources, and time? What might get in my way?",
      example: "If you haven't exercised in years, starting with 30 minutes 5x/week is achievable. Running a marathon next month is not."
    },
    {
      letter: "R",
      word: "Relevant",
      description: "Your goal should matter to YOU and align with your broader life objectives and values.",
      question: "Why does this goal matter to me? How does it fit with my other priorities?",
      example: "Morning walks support your goal of having more energy and reducing stress—both relevant to thriving in your 40s."
    },
    {
      letter: "T",
      word: "Time-Bound",
      description: "Set a deadline. Open-ended goals tend to get pushed aside by daily demands.",
      question: "When will I achieve this? What's my deadline? What can I do today? This week?",
      example: "I will complete 30-minute morning walks 5 days a week for the next 12 weeks of this program."
    }
  ];

  const sampleGoals = [
    {
      category: "Health & Wellness",
      poor: "I want to be healthier.",
      smart: "I will drink 64 oz of water daily and track my intake in a journal for the next 30 days."
    },
    {
      category: "Personal Growth",
      poor: "I want to read more.",
      smart: "I will read for 20 minutes before bed every night, finishing one book per month for the next 3 months."
    },
    {
      category: "Relationships",
      poor: "I want to spend more time with family.",
      smart: "I will have a phone-free family dinner at the table every Sunday evening for the next 12 weeks."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            Week 1 • Day 2 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            The SMART Goal Framework
          </h1>
          <p className="text-xl text-secondary-600">
            How to Write Goals That Actually Stick
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/6ef295fae6717eb0d0388c963cc3d83f92bd8096130cf54f22395136abb2f6dc.png"
            alt="Goal Setting Workspace"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Most Goals Fail (And How Yours Won't)
          </h2>
          <p className="text-secondary-700 mb-4">
            Research shows that people who write down specific goals are <strong>42% more likely to achieve them</strong> than those who simply think about what they want. The difference? Clarity.
          </p>
          <p className="text-secondary-700 mb-4">
            The SMART framework transforms vague wishes into actionable plans. It's not about setting easy goals—it's about setting <em>clear</em> goals that your brain can actually work toward.
          </p>
          <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
            <p className="text-secondary-800 font-medium flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              In your 40s, you have more clarity about what truly matters. Use that wisdom to set goals that align with the life you actually want—not the life others expect.
            </p>
          </div>
        </section>

        {/* SMART Framework */}
        <section className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 text-center">
            The SMART Framework Explained
          </h2>
          <div className="space-y-4">
            {smartFramework.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-white">{item.letter}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-secondary-900 mb-2">{item.word}</h3>
                    <p className="text-secondary-700 mb-3">{item.description}</p>
                    <div className="bg-secondary-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-secondary-600">
                        <strong>Ask yourself:</strong> {item.question}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <p className="text-sm text-green-800">
                        <strong>Example:</strong> {item.example}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Sample Goals */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Transforming Wishes into SMART Goals
          </h2>
          <div className="space-y-6">
            {sampleGoals.map((goal, index) => (
              <div key={index} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                <h3 className="font-bold text-primary mb-3">{goal.category}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-xs font-semibold text-red-600 uppercase mb-2">❌ Vague Goal</p>
                    <p className="text-secondary-700 italic">"{goal.poor}"</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-xs font-semibold text-green-600 uppercase mb-2">✓ SMART Goal</p>
                    <p className="text-secondary-700">"{goal.smart}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Your Turn */}
        <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 rounded-2xl p-8 border-2 border-primary/20">
          <div className="text-center mb-6">
            <Target className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">
              Your Turn: Set 3 SMART Goals
            </h2>
            <p className="text-secondary-600">
              Use the framework above to define 3 meaningful goals for your 12-week journey
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-bold text-secondary-900 mb-4">Goal-Setting Prompts:</h3>
            <ul className="space-y-3">
              {[
                "What's one health habit you've been wanting to establish?",
                "What relationship would you like to strengthen, and how?",
                "What's one thing you'd do if you knew you couldn't fail?",
                "What would make you proud to accomplish in the next 12 weeks?",
                "What's holding you back, and what small step could address it?"
              ].map((prompt, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-secondary-700">{prompt}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-center text-secondary-600 mt-6 text-sm">
            Write your goals in your journal, then revisit them weekly to track progress.
          </p>
        </section>
      </main>
    </div>
  );
}

export default SmartGoalsGuide;
